import { useState } from 'react';
import { INSURANCE } from './mockData';
import type { ClaimRow } from './types';
import { daysUntil, fmtDate, money } from './utils';
import { SectionHead, SourceMarkers } from './primitives';

const fx = INSURANCE;
const planName = (id: string) => fx.plans.find((p) => p.id === id)?.carrier ?? id;

/* ── 4.6 Benefits left this year ─────────────────────────────────────── */
export function BenefitsLeft() {
  return (
    <section id="ins-benefits" className="ins-section">
      <SectionHead title="Benefits left this year" />
      <div className="ins-grid ins-grid-2">
        {fx.benefits.map((b) => {
          const pct = Math.max(2, Math.round((b.remaining / b.annualMax) * 100));
          const days = daysUntil(b.resets, fx.today);
          const plan = fx.plans.find((p) => p.id === b.planId);
          return (
            <div className="ins-card" key={b.planId}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div className="ins-plan-eyebrow">{plan?.order === 'primary' ? 'Primary' : 'Secondary'} · {planName(b.planId)}</div>
                <span className="ins-note" style={{ marginTop: 0 }}>Resets {fmtDate(b.resets)} · {days} days</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, marginTop: 8 }}>{money(b.remaining)}</div>
              <div className="ins-note" style={{ marginTop: 0 }}>left of {money(b.annualMax)} annual maximum</div>
              <div
                className="ins-bar"
                role="progressbar"
                aria-valuenow={b.used}
                aria-valuemin={0}
                aria-valuemax={b.annualMax}
                aria-label={`${money(b.used)} used of ${money(b.annualMax)} — ${money(b.remaining)} left`}
              >
                <div className="ins-bar-fill ins-bar-used" style={{ width: `${String(100 - pct)}%` }} />
              </div>
              <div className="ins-bar-ends"><span>{money(b.used)} used</span><span>{money(b.remaining)} left</span></div>
              {b.deductibleLine ? <div className="ins-note" style={{ marginTop: 4 }}>{b.deductibleLine}</div> : null}
              {b.familyDeductibleLine ? <div className="ins-note" style={{ marginTop: 2 }}>{b.familyDeductibleLine}</div> : null}
              {b.noDeductibleNote ? <div className="ins-note" style={{ marginTop: 4 }}>{b.noDeductibleNote}</div> : null}
              {b.notCounted ? (
                <div className="ins-note" style={{ marginTop: 8 }}>{b.notCounted.text} <SourceMarkers sources={b.notCounted.sources} factTitle={b.notCounted.text} /></div>
              ) : null}
              {b.unconfirmed ? (
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--color-warning-soft)' }}>
                  <span className="ins-tag ins-tag-open">Not yet confirmed</span>
                  <div className="ins-note" style={{ marginTop: 4, color: 'var(--color-warning)' }}>{b.unconfirmed.title}. {b.unconfirmed.detail} <SourceMarkers sources={b.unconfirmed.sources} factTitle={b.unconfirmed.title} /></div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── 4.7 What the plan covers ────────────────────────────────────────── */
export function PlanCoverage() {
  const [plan, setPlan] = useState('sunlife');
  const rows = fx.coverage[plan] ?? [];
  return (
    <section id="ins-coverage" className="ins-section">
      <SectionHead title="What the plan covers" right={<PlanSwitch value={plan} onChange={setPlan} />} />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">What the plan covers</caption>
          <thead>
            <tr><th scope="col">Service</th><th scope="col">Covered</th><th scope="col">Rules</th><th scope="col">For {fx.patient.name.split(' ')[0]}</th><th scope="col">Source</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  <div className="strong">{r.service}</div>
                  {r.subLabel ? <div className="ins-sub">{r.subLabel}</div> : null}
                </td>
                <td>
                  {r.covered}
                  {r.officeFee != null ? <div className="ins-sub">Patient pays — office fee {money(r.officeFee)} (this office’s fee)</div> : null}
                </td>
                <td>{r.rules}</td>
                <td><span className={`ins-tag ${r.forPatient.verdict === 'applies' ? 'ins-tag-applies' : r.forPatient.verdict === 'age' ? 'ins-tag-age' : 'ins-tag-excl'}`}>{r.forPatient.text}</span></td>
                <td><SourceMarkers sources={r.sources} factId={r.factId} factTitle={r.service} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── 4.8 Plan rules ──────────────────────────────────────────────────── */
export function PlanRules() {
  return (
    <section id="ins-rules" className="ins-section">
      <SectionHead title="Plan rules" sub="how this plan pays, beyond the percentages" />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">Plan rules</caption>
          <thead>
            <tr><th scope="col">Rule</th><th scope="col">What it means for {fx.patient.name.split(' ')[0]}</th><th scope="col">How ELVA knows</th><th scope="col">Source</th></tr>
          </thead>
          <tbody>
            {fx.planRules.map((r) => (
              <tr key={r.id}>
                <td>
                  <div className="strong">{r.rule}</div>
                  {r.badge === 'needs_clarification' ? <span className="ins-tag ins-tag-pending" style={{ marginTop: 4 }}>Needs clarification</span> : null}
                  {r.badge === 'office_experience' ? <span className="ins-tag ins-tag-notyet" style={{ marginTop: 4 }}>Office experience</span> : null}
                </td>
                <td>{r.meaning}</td>
                <td>{r.howKnown}</td>
                <td>{r.notYetKnown ? <span className="ins-tag ins-tag-notyet">Not yet known</span> : <SourceMarkers sources={r.sources} factId={r.factId} factTitle={r.rule} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── 4.9 When each service is next covered ───────────────────────────── */
export function FrequencyTable() {
  return (
    <section id="ins-frequency" className="ins-section">
      <SectionHead title="When each service is next covered" />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">When each service is next covered</caption>
          <thead>
            <tr><th scope="col">Service</th><th scope="col">Rule</th><th scope="col">Last done</th><th scope="col">Next covered</th><th scope="col">Status</th></tr>
          </thead>
          <tbody>
            {fx.frequency.map((r, i) => (
              <tr key={i}>
                <td>
                  <div className="strong">{r.service}</div>
                  {r.badge === 'needs_clarification' ? <span className="ins-tag ins-tag-pending" style={{ marginTop: 4 }}>Needs clarification</span> : null}
                </td>
                <td>{r.rule}</td>
                <td>{r.lastDone}</td>
                <td>{r.nextCovered}</td>
                <td>
                  <span className={`ins-tag ${r.status === 'covered' || r.status === 'no_limit' ? 'ins-tag-covered' : r.status === 'cannot_compute' ? 'ins-tag-pending' : 'ins-tag-notyet'}`}>
                    {r.status === 'covered' ? 'Covered' : r.status === 'no_limit' ? 'No limit (verified)' : r.status === 'cannot_compute' ? 'Cannot compute' : 'Not yet'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ins-note" style={{ marginTop: 8 }}>Last-done dates come from this office’s records. Work done at another office shows only if the insurer reports it. A limit stated as a standard rather than a number is shown as a note and never turned into a count.</p>
    </section>
  );
}

const PT_STATUS: Record<string, { label: string; cls: string }> = {
  not_booked: { label: 'Not booked', cls: 'ins-tag-notyet' },
  preauth_pending: { label: 'Pre-auth pending', cls: 'ins-tag-pending' },
  booked: { label: 'Booked', cls: 'ins-tag-approved' },
};

/* ── 4.10 Planned treatment ──────────────────────────────────────────── */
export function PlannedTreatment() {
  const t = fx.plannedTreatment;
  return (
    <section id="ins-planned" className="ins-section">
      <SectionHead title="Planned treatment" sub={t.totalLabel} />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">Planned treatment</caption>
          <thead>
            <tr><th scope="col">Procedure</th><th scope="col" className="num">Fee</th><th scope="col" className="num">Insurance</th><th scope="col" className="num">Patient</th><th scope="col">Status</th></tr>
          </thead>
          <tbody>
            {t.rows.map((r, i) => {
              const s = PT_STATUS[r.status];
              const pending = r.insuranceKnown === false;
              return (
                <tr key={i}>
                  <td>
                    <div className="strong">{r.procedure}</div>
                    {r.cappedNote ? <div className="ins-sub" style={{ color: 'var(--color-warning)' }}>{r.cappedNote}</div> : null}
                    {pending ? <div className="ins-sub">Decision expected {r.decisionExpected}</div> : null}
                  </td>
                  <td className="num">{money(r.fee)}</td>
                  <td className="num">{pending ? 'Not yet known' : money(r.insurance)}</td>
                  <td className="num">{pending ? 'Not yet known' : money(r.patient)}</td>
                  <td><span className={`ins-tag ${s?.cls ?? ''}`}>{s?.label}{r.statusDate ? ` ${r.statusDate}` : ''}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="ins-note" style={{ marginTop: 8 }}>Insurance amounts use the benefits left today. The order treatment is booked in changes them.</p>
    </section>
  );
}

const PA_STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'ins-tag-pending' },
  approved: { label: 'Approved', cls: 'ins-tag-approved' },
  used: { label: 'Used', cls: 'ins-tag-used' },
  expired: { label: 'Expired', cls: 'ins-tag-notyet' },
};

/* ── 4.10 Pre-authorizations ─────────────────────────────────────────── */
export function PreAuths() {
  return (
    <section id="ins-preauths" className="ins-section">
      <SectionHead title="Pre-authorizations" />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">Pre-authorizations</caption>
          <thead>
            <tr><th scope="col">Procedure</th><th scope="col">Reference</th><th scope="col">Response</th><th scope="col">Valid until</th><th scope="col">Status</th></tr>
          </thead>
          <tbody>
            {fx.preAuths.map((r, i) => {
              const s = PA_STATUS[r.status];
              return (
                <tr key={i}>
                  <td className="strong">{r.procedure}</td>
                  <td>{r.reference}</td>
                  <td>{r.response}</td>
                  <td>{r.validUntil}</td>
                  <td>
                    <span className={`ins-tag ${s?.cls ?? ''}`}>{s?.label}{r.statusDate ? ` ${r.statusDate}` : ''}</span>
                    {r.source ? <div style={{ marginTop: 4 }}><SourceMarkers sources={[r.source]} factTitle={r.procedure} /></div> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const CLAIM_FILTERS = ['All', 'Paid', 'Open', 'Denied'] as const;

/* ── 4.10 Claims ─────────────────────────────────────────────────────── */
export function Claims() {
  const [filter, setFilter] = useState<(typeof CLAIM_FILTERS)[number]>('All');
  const rows = fx.claims.filter((c: ClaimRow) => filter === 'All' || c.status === filter.toLowerCase());
  return (
    <section id="ins-claims" className="ins-section">
      <SectionHead title="Claims" right={
        <span className="ins-filter">
          {CLAIM_FILTERS.map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => { setFilter(f); }}>{f}</button>
          ))}
        </span>
      } />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">Claims</caption>
          <thead>
            <tr><th scope="col">Service date</th><th scope="col">Claim</th><th scope="col">Procedure</th><th scope="col" className="num">Billed</th><th scope="col" className="num">Allowed</th><th scope="col" className="num">Insurance</th><th scope="col" className="num">Patient</th><th scope="col">Status</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.claim}>
                <td>{c.serviceDate}</td>
                <td><button type="button" className="ins-link">{c.claim}</button></td>
                <td>{c.procedure}</td>
                <td className="num">{money(c.billed)}</td>
                <td className="num">{money(c.allowed)}</td>
                <td className="num">{money(c.insurance)}</td>
                <td className="num">{money(c.patient)}</td>
                <td>
                  <span className={`ins-tag ${c.status === 'paid' ? 'ins-tag-paid' : c.status === 'denied' ? 'ins-tag-denied' : 'ins-tag-pending'}`}>
                    {c.status === 'paid' ? 'Paid' : c.status === 'denied' ? 'Denied' : 'Open'}
                  </span>
                  {c.deniedReason ? <span className="ins-note" style={{ marginTop: 0 }}> · {c.deniedReason}</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ins-note" style={{ marginTop: 8 }}>Benefits are estimates, not a guarantee of payment. The insurer decides when the claim is processed.</p>
    </section>
  );
}

/* ── 4.6b Shared allowances (D1) ─────────────────────────────────────── */
export function SharedAllowances() {
  return (
    <section id="ins-allowances" className="ins-section">
      <SectionHead title="Shared allowances" sub="where several procedures draw on one limit — using any one of them uses the allowance" />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">Shared allowances</caption>
          <thead>
            <tr><th scope="col">Allowance</th><th scope="col">Draws on it</th><th scope="col">Limit</th><th scope="col">Used</th><th scope="col">Left</th><th scope="col">Source</th></tr>
          </thead>
          <tbody>
            {fx.allowances.map((a, i) => {
              const unknown = a.left.toLowerCase().startsWith('unknown');
              return (
                <tr key={i}>
                  <td>
                    <div className="strong">{a.allowance}</div>
                    {a.note ? <div className="ins-sub">{a.note}</div> : null}
                  </td>
                  <td>{a.drawsOn}</td>
                  <td>{a.limit}</td>
                  <td>{a.used}</td>
                  <td className={unknown ? '' : 'strong'}>{unknown ? <span className="ins-tag ins-tag-notyet">{a.left}</span> : a.left}</td>
                  <td><SourceMarkers sources={a.sources} factTitle={a.allowance} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="ins-note" style={{ marginTop: 8 }}>&ldquo;Left&rdquo; counts what this office used and whatever Sunlife reports as used. Work done at another office shows only if the insurer reports it — when it might not, this reads unknown rather than a number.</p>
    </section>
  );
}

const PREREQ_SEV: Record<string, { label: string; cls: string }> = {
  blocks: { label: 'Blocks submission', cls: 'ins-tag-denied' },
  date: { label: 'Changes the date', cls: 'ins-tag-pending' },
  warns: { label: 'Warns', cls: 'ins-tag-notyet' },
};

/* ── 4.8b Before a claim goes out (D2) ───────────────────────────────── */
export function BeforeClaimGoesOut() {
  return (
    <section id="ins-prereqs" className="ins-section">
      <SectionHead title="Before a claim goes out" sub="instructions for the team — not benefits, and not on the patient's copy" />
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">Before a claim goes out</caption>
          <thead>
            <tr><th scope="col">Requirement</th><th scope="col">Applies to</th><th scope="col">What ELVA does</th><th scope="col">Source</th></tr>
          </thead>
          <tbody>
            {fx.claimPrereqs.map((c, i) => {
              const sev = PREREQ_SEV[c.severity];
              return (
                <tr key={i}>
                  <td>
                    <div className="strong">{c.requirement}</div>
                    <span className={`ins-tag ${sev?.cls ?? ''}`} style={{ marginTop: 4 }}>{sev?.label}</span>
                  </td>
                  <td>{c.appliesTo}</td>
                  <td>{c.whatElvaDoes}</td>
                  <td><SourceMarkers sources={c.sources} factTitle={c.requirement} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Shared plan switcher (coverage + sources use independent instances). */
export function PlanSwitch({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  return (
    <span className="ins-switch" role="group" aria-label="Plan">
      {fx.plans.map((p) => (
        <button key={p.id} type="button" aria-pressed={value === p.id} onClick={() => { onChange(p.id); }}>
          {p.carrier} · {p.order === 'primary' ? 'primary' : 'secondary'}
        </button>
      ))}
    </span>
  );
}
